import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
/* React Router Link removed: booking links now point to external BR OS app */
import { Loader2, ArrowUpDown, ChevronUp, ChevronRight, ChevronDown, FileText } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Badge } from './ui/badge';
import { formatCurrency } from '../lib/utils';
import { resolveDepositDisplayStatus } from '../lib/depositStatus';
import { reportsAPI, diagnosticsAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

// Status chip colours
const STATUS_COLORS = {
  fully_invoiced: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  partially_invoiced: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  not_invoiced: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  over_invoiced: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  on_schedule: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
};

const STATUS_LABELS = {
  fully_invoiced: 'Fully invoiced',
  partially_invoiced: 'Partially invoiced',
  not_invoiced: 'Not invoiced',
  over_invoiced: 'Over-invoiced',
  on_schedule: 'Billed up to date',
};

const REASON_LABELS = {
  invoiced_in_other_month: 'Invoiced in other month',
  cancelled_booking: 'Cancelled booking',
  credit_note_applied: 'Credit note applied',
  draft_exists_not_finalized: 'Draft (not finalized)',
  voided_invoice: 'Voided invoice',
  stay_in_progress: 'Stay still running',
  unexplained: 'Unexplained',
};

function StatusChip({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-800'}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function ReasonBadges({ reasons }) {
  if (!reasons?.length) return <span className="text-xs text-muted-foreground">-</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {reasons.map((r) => (
        <Badge
          key={r}
          variant={r === 'unexplained' ? 'destructive' : 'outline'}
          className="text-xs"
        >
          {REASON_LABELS[r] || r}
        </Badge>
      ))}
    </div>
  );
}

// Sortable column header
function SortableColumnHead({ field, sortField, sortAsc, onToggle, children, className }) {
  return (
    <TableHead
      className={`cursor-pointer select-none hover:text-foreground ${className || ''}`}
      onClick={() => onToggle(field)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortField === field && (
          <ArrowUpDown className="h-3 w-3" />
        )}
      </span>
    </TableHead>
  );
}


/** Shared linked-invoices expansion row. */
function InvoiceExpansionRow({ row, dc }) {
  return (
    <TableRow className="bg-muted/30">
      <TableCell colSpan={9} className="p-0">
        <div className="px-6 py-2">
          <p className="text-xs font-medium text-muted-foreground mb-1">Linked invoices</p>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground">
                <th className="text-left py-1">Invoice ID</th>
                <th className="text-left py-1">Issue Date</th>
                <th className="text-left py-1">Status</th>
                <th className="text-left py-1">Type</th>
                <th className="text-right py-1">Amount</th>
                <th className="text-center py-1">PDF</th>
              </tr>
            </thead>
            <tbody>
              {(row.linked_invoices || []).map((inv) => (
                <tr key={inv.invoice_id} className={inv.voided ? 'opacity-50 line-through' : ''}>
                  <td className="py-1 font-mono">{inv.invoice_id}</td>
                  <td className="py-1">{(inv.issue_date || '').slice(0, 10)}</td>
                  <td className="py-1">
                    <Badge variant="outline" className="text-xs">
                      {inv.status}
                    </Badge>
                    {inv.voided && <Badge variant="destructive" className="text-xs ml-1">voided</Badge>}
                  </td>
                  <td className="py-1">{inv.type || '-'}</td>
                  <td className="py-1 text-right font-mono">
                    {formatCurrency(inv.amount, dc)}
                  </td>
                  <td className="py-1 text-center">
                    {inv.has_xero_pdf ? (
                      <button
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const res = await diagnosticsAPI.fetchBrosInvoiceXeroPdf(inv.invoice_id);
                            const blob = new Blob([res.data], { type: 'application/pdf' });
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank');
                          } catch (err) {
                            toast.error(getErrorMessage(err) || 'Failed to fetch PDF');
                          }
                        }}
                      >
                        <FileText className="h-3 w-3" />
                        View PDF
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground cursor-default" title="No Xero PDF on this invoice">
                        <FileText className="h-3 w-3 inline opacity-30" />
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * Reconciliation drill-down panel rendered inline below the chart.
 * Replaces the previous modal. Props:
 *   expanded  boolean   whether the panel is shown
 *   onClose   ()=>void  collapse callback
 *   month     string    YYYY-MM key
 *   dc        string    display currency
 *   rollup    object    monthly rollup from reconciliation endpoint
 */
export function ReconciliationDrilldown({ expanded, onClose, month, dc, rollup }) {
  const [rows, setRows] = useState([]);
  const [uninvoicedRoots, setUninvoicedRoots] = useState([]);
  const [unmatchedInvoices, setUnmatchedInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState('delta');
  const [sortAsc, setSortAsc] = useState(true);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [expandedRoot, setExpandedRoot] = useState(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!expanded || !month) return;
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setRows([]);
      setUninvoicedRoots([]);
      setUnmatchedInvoices([]);
      setExpandedBooking(null);
      setExpandedRoot(null);
      setStatusFilter('');
      try {
        const { data } = await reportsAPI.brosBookingInvoiceReconDrilldown({
          month,
          currency: (dc || 'GBP').toLowerCase(),
        });
        if (cancelled) return;
        setRows(data.rows || []);
        setUninvoicedRoots(data.uninvoiced_roots || []);
        setUnmatchedInvoices(data.unmatched_invoices || []);
      } catch (err) {
        if (!cancelled) toast.error(getErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [expanded, month, dc]);

  // Scroll into view when panel expands
  useEffect(() => {
    if (expanded && panelRef.current) {
      // Small delay so the DOM has rendered the content
      const timer = setTimeout(() => {
        panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [expanded, month]);

  const toggleSort = useCallback((field) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortAsc((a) => !a);
        return field;
      }
      setSortAsc(field === 'booking_id');
      return field;
    });
  }, []);

  // Group rows by root_group_id so each enquiry is ONE root row.
  // Siblings are expandable beneath, labelled as pro-rata allocation.
  // Grain-of-presentation only: no reconciliation math changes.
  const groupedRows = useMemo(() => {
    if (!rows.length) return [];
    const groups = new Map();
    for (const row of rows) {
      const rootId = row.root_group_id || row.booking_id;
      if (!groups.has(rootId)) {
        groups.set(rootId, []);
      }
      groups.get(rootId).push(row);
    }
    return Array.from(groups.entries()).map(([rootId, siblings]) => {
      if (siblings.length === 1) {
        return { rootId, rootRow: siblings[0], siblings: [], isGroup: false };
      }
      // Multi-sibling: collapsed root row with summed values
      const groupClosed = siblings.reduce((s, r) => s + (r.booking_value || 0), 0);
      const groupInvoiced = Number(siblings[0].group_invoiced || 0);
      const delta = groupInvoiced - groupClosed;
      const first = siblings[0];
      return {
        rootId,
        rootRow: {
          ...first,
          booking_id: rootId,
          booking_value: groupClosed,
          invoiced_total: groupInvoiced,
          delta,
          reasons: [],
        },
        siblings: [...siblings].sort((a, b) => (a.booking_id > b.booking_id ? 1 : -1)),
        isGroup: true,
      };
    });
  }, [rows]);

  const sorted = useMemo(() => {
    if (!groupedRows.length) return [];
    const filtered = statusFilter
      ? groupedRows.filter((g) => g.rootRow.recon_status === statusFilter)
      : groupedRows;
    return [...filtered].sort((a, b) => {
      let va = a.rootRow[sortField];
      let vb = b.rootRow[sortField];
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va == null) va = '';
      if (vb == null) vb = '';
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [groupedRows, sortField, sortAsc, statusFilter]);

  // Status filter options derived from grouped data (root grain)
  const statusCounts = useMemo(() => {
    const counts = {};
    for (const g of groupedRows) {
      counts[g.rootRow.recon_status] = (counts[g.rootRow.recon_status] || 0) + 1;
    }
    return counts;
  }, [groupedRows]);

  if (!expanded) return null;

  const sharedHeadProps = { sortField, sortAsc, onToggle: toggleSort };
  const monthLabel = month || '';

  return (
    <div
      ref={panelRef}
      className="mt-4 border-t pt-4 animate-in fade-in slide-in-from-top-2 duration-200"
      role="region"
      aria-label={`Booking reconciliation for ${monthLabel}`}
    >
      {/* Header with close button */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-sm font-semibold">
            Booking Reconciliation ({monthLabel})
          </h4>
          <p className="text-xs text-muted-foreground">
            Per-booking reconciliation: what was closed in {monthLabel} vs what has been invoiced against those bookings.
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground p-1 rounded hover:bg-muted transition-colors"
          aria-label="Close reconciliation panel"
        >
          <ChevronUp className="h-4 w-4" />
        </button>
      </div>

      {/* KPI row */}
      {rollup && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
          <div className="border rounded-lg p-3">
            <p className="text-xs text-muted-foreground">Closed</p>
            <p className="text-lg font-semibold font-mono">{formatCurrency(rollup.closed_value, dc)}</p>
            <p className="text-xs text-muted-foreground">{rollup.booking_count} booking(s)</p>
          </div>
          <div className="border rounded-lg p-3">
            <p className="text-xs text-muted-foreground">Invoiced (against these)</p>
            <p className="text-lg font-semibold font-mono">{formatCurrency(rollup.invoiced_against_bookings, dc)}</p>
          </div>
          <div className="border rounded-lg p-3">
            <p className="text-xs text-muted-foreground">Coverage</p>
            <p className="text-lg font-semibold font-mono">{rollup.coverage_pct}%</p>
          </div>
          <div className={`border rounded-lg p-3 ${rollup.real_residual != null && rollup.real_residual !== 0 ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/20' : ''}`}>
            <p className="text-xs text-muted-foreground">Real residual</p>
            <p className={`text-lg font-semibold font-mono ${rollup.real_residual != null && rollup.real_residual !== 0 ? 'text-amber-600 dark:text-amber-400' : ''}`}>
              {formatCurrency(rollup.real_residual ?? rollup.unexplained_delta, dc)}
            </p>
          </div>
          {rollup.unmatched_invoice_count > 0 && (
            <div className="border rounded-lg p-3 border-red-300 bg-red-50 dark:bg-red-950/20">
              <p className="text-xs text-muted-foreground">Unmatched invoices</p>
              <p className="text-lg font-semibold font-mono text-red-600 dark:text-red-400">
                {formatCurrency(rollup.unmatched_invoice_total, dc)}
              </p>
              <p className="text-xs text-muted-foreground">{rollup.unmatched_invoice_count} invoice(s)</p>
            </div>
          )}
        </div>
      )}

      {/* Bookings without invoices (root-grain, date-free) */}
      {uninvoicedRoots.length > 0 && (
        <div className="mb-3" data-testid="uninvoiced-roots-section">
          <div className="border rounded-lg p-3 border-amber-300 bg-amber-50 dark:bg-amber-950/20">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-amber-800 dark:text-amber-400">
                Bookings without invoices (root-grain, all dates)
              </p>
              <span className="text-xs text-muted-foreground">
                {uninvoicedRoots.length} root(s) to chase
              </span>
            </div>
            <p className="text-lg font-semibold font-mono text-amber-600 dark:text-amber-400">
              {formatCurrency(
                uninvoicedRoots.reduce((s, r) => s + Number(r.expected_ex_vat ?? 0), 0),
                dc
              )}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">
              Root-grain: siblings covered by their root's consolidated invoice are excluded.
              This count spans all dates (not month-scoped like the table above).
            </p>
            <div className="mt-2 max-h-48 overflow-y-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-muted-foreground">
                    <th className="text-left py-1">Root ID</th>
                    <th className="text-left py-1">Currency</th>
                    <th className="text-right py-1">Expected (ex-VAT)</th>
                    <th className="text-left py-1">Siblings</th>
                  </tr>
                </thead>
                <tbody>
                  {uninvoicedRoots.map((r) => (
                    <tr key={r.root_id}>
                      <td className="py-1 font-mono">{r.root_id}</td>
                      <td className="py-1">{r.currency}</td>
                      <td className="py-1 text-right font-mono">{formatCurrency(Number(r.expected_ex_vat), r.currency)}</td>
                      <td className="py-1 text-muted-foreground">{(r.sibling_booking_ids ?? []).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Invoices without bookings (all dates, from matcher) */}
      {unmatchedInvoices.length > 0 && (() => {
        // Classification keys on presence of orphan_booking_id field,
        // NOT truthiness. The matcher is the single source; the frontend
        // reads but does not recompute this population.
        const orphans = unmatchedInvoices.filter((u) => Object.prototype.hasOwnProperty.call(u, 'orphan_booking_id'));
        const adHocs = unmatchedInvoices.filter((u) => !Object.prototype.hasOwnProperty.call(u, 'orphan_booking_id'));
        return (
          <div className="mb-3" data-testid="unmatched-invoices-section">
            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium" data-testid="unmatched-invoices-title">
                  Invoices without bookings (all dates)
                </p>
                <span className="text-xs text-muted-foreground">
                  {unmatchedInvoices.length} invoice(s)
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground mb-3">
                Invoices with no matched booking. This count spans all dates (not month-scoped like the table above).
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Ad-hoc invoices (no orphan_booking_id) */}
                <div className="border rounded-lg p-3" data-testid="unmatched-ad-hoc-group">
                  <p className="text-xs font-medium text-muted-foreground" data-testid="ad-hoc-label">
                    Ad-hoc
                  </p>
                  <p className="text-lg font-semibold font-mono" data-testid="ad-hoc-total">
                    {formatCurrency(
                      adHocs.reduce((s, u) => s + Number(u.subtotal_gbp ?? 0), 0),
                      'GBP'
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground" data-testid="ad-hoc-count">{adHocs.length} invoice(s)</p>
                  {adHocs.length > 0 && (
                    <div className="mt-2 max-h-32 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-muted-foreground">
                            <th className="text-left py-1">Invoice</th>
                            <th className="text-right py-1">Amount (GBP)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {adHocs.map((u) => (
                            <tr key={u.invoice_id} data-testid="ad-hoc-row">
                              <td className="py-1 font-mono">{u.invoice_id}</td>
                              <td className="py-1 text-right font-mono">{formatCurrency(Number(u.subtotal_gbp ?? 0), 'GBP')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Orphan invoices (has orphan_booking_id) */}
                <div className="border rounded-lg p-3 border-red-300 bg-red-50 dark:bg-red-950/20" data-testid="unmatched-orphan-group">
                  <p className="text-xs font-medium text-red-800 dark:text-red-400" data-testid="orphan-label">
                    Orphan
                  </p>
                  <p className="text-lg font-semibold font-mono text-red-600 dark:text-red-400" data-testid="orphan-total">
                    {formatCurrency(
                      orphans.reduce((s, u) => s + Number(u.subtotal_gbp ?? 0), 0),
                      'GBP'
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground" data-testid="orphan-count">{orphans.length} invoice(s)</p>
                  {orphans.length > 0 && (
                    <div className="mt-2 max-h-32 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-muted-foreground">
                            <th className="text-left py-1">Invoice</th>
                            <th className="text-right py-1">Amount (GBP)</th>
                            <th className="text-left py-1">Booking ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orphans.map((u) => (
                            <tr key={u.invoice_id} data-testid="orphan-row">
                              <td className="py-1 font-mono">{u.invoice_id}</td>
                              <td className="py-1 text-right font-mono">{formatCurrency(Number(u.subtotal_gbp ?? 0), 'GBP')}</td>
                              <td className="py-1 font-mono text-muted-foreground">{u.orphan_booking_id}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2 mb-3">
        <button
          onClick={() => setStatusFilter('')}
          className={`text-xs px-2 py-1 rounded-full border ${!statusFilter ? 'bg-foreground text-background' : 'hover:bg-muted'}`}
        >
          All ({groupedRows.length})
        </button>
        {Object.entries(STATUS_LABELS).map(([key, label]) => {
          const count = statusCounts[key] || 0;
          if (count === 0 && statusFilter !== key) return null;
          return (
            <button
              key={key}
              onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
              className={`text-xs px-2 py-1 rounded-full border ${statusFilter === key ? 'bg-foreground text-background' : 'hover:bg-muted'}`}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && sorted.length === 0 && (
        <p className="text-sm text-muted-foreground py-4 text-center">No bookings for this month.</p>
      )}

      {!loading && sorted.length > 0 && (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableColumnHead field="booking_id" {...sharedHeadProps}>Booking</SortableColumnHead>
                <SortableColumnHead field="client" {...sharedHeadProps}>Client</SortableColumnHead>
                <SortableColumnHead field="booking_value" className="text-right" {...sharedHeadProps}>Closed</SortableColumnHead>
                <SortableColumnHead field="invoiced_total" className="text-right" {...sharedHeadProps}>Invoiced</SortableColumnHead>
                <SortableColumnHead field="delta" className="text-right" {...sharedHeadProps}>Delta</SortableColumnHead>
                <TableHead className="text-right">Deposit</TableHead>
                <SortableColumnHead field="recon_status" {...sharedHeadProps}>Status</SortableColumnHead>
                <TableHead>Reason</TableHead>
                <TableHead className="text-right">Invoices</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((group) => (
                <React.Fragment key={group.rootId}>
                  {/* Root row (or single-booking row) */}
                  <TableRow
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      if (group.isGroup) {
                        setExpandedRoot(expandedRoot === group.rootId ? null : group.rootId);
                      } else {
                        setExpandedBooking(expandedBooking === group.rootRow.booking_id ? null : group.rootRow.booking_id);
                      }
                    }}
                  >
                    <TableCell className="font-mono text-sm">
                      <span className="inline-flex items-center gap-1">
                        {group.isGroup && (
                          expandedRoot === group.rootId
                            ? <ChevronDown className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                            : <ChevronRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                        )}
                        <a
                          href={`https://internal.bonjour-residences.co.uk/bookings/${encodeURIComponent(group.rootRow.booking_id)}`}
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="recon-booking-link"
                        >
                          {group.rootRow.booking_id || '(no id)'}
                        </a>
                        {group.isGroup && (
                          <span className="text-[10px] text-muted-foreground">
                            ({group.siblings.length} segments)
                          </span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm truncate max-w-[140px]" title={group.rootRow.client}>
                      {group.rootRow.client}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatCurrency(group.rootRow.booking_value, dc)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatCurrency(group.rootRow.invoiced_total, dc)}
                    </TableCell>
                    <TableCell className={`text-right font-mono text-sm ${group.rootRow.delta > 0 ? 'text-purple-600' : group.rootRow.delta < 0 ? 'text-red-600' : ''}`}>
                      {group.rootRow.delta > 0 ? '+' : ''}{formatCurrency(group.rootRow.delta, dc)}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {Number(group.rootRow.deposit?.client_amount ?? 0) > 0 ? (
                        <span className="font-mono" title={`Status: ${resolveDepositDisplayStatus(group.rootRow.deposit)}, Billed: ${group.rootRow.deposit?.billed ? 'yes' : 'no'}`}>
                          {formatCurrency(group.rootRow.deposit.client_amount, dc)}
                          <span className={`ml-1 text-xs ${group.rootRow.deposit?.billed ? 'text-green-600' : 'text-amber-600'}`}>
                            {resolveDepositDisplayStatus(group.rootRow.deposit)}
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={group.rootRow.recon_status} />
                    </TableCell>
                    <TableCell>
                      <ReasonBadges reasons={group.rootRow.reasons} />
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {group.rootRow.linked_invoices?.length || 0}
                    </TableCell>
                  </TableRow>

                  {/* Single-booking: show linked invoices when expanded */}
                  {!group.isGroup && expandedBooking === group.rootRow.booking_id && group.rootRow.linked_invoices?.length > 0 && (
                    <InvoiceExpansionRow row={group.rootRow} dc={dc} />
                  )}

                  {/* Multi-sibling group: show siblings when expanded */}
                  {group.isGroup && expandedRoot === group.rootId && (
                    <>
                      <TableRow className="bg-blue-50/30 dark:bg-blue-950/10">
                        <TableCell colSpan={9} className="py-1 px-6">
                          <p className="text-[11px] text-muted-foreground font-medium" data-testid="pro-rata-label">
                            Pro-rata allocation across {group.siblings.length} segments (presentation only, not observed at segment grain)
                          </p>
                        </TableCell>
                      </TableRow>
                      {group.siblings.map((row) => (
                        <React.Fragment key={row.booking_id}>
                          <TableRow
                            className="bg-blue-50/20 dark:bg-blue-950/5 cursor-pointer hover:bg-muted/50"
                            onClick={() => setExpandedBooking(expandedBooking === row.booking_id ? null : row.booking_id)}
                            data-testid="sibling-row"
                          >
                            <TableCell className="font-mono text-sm pl-8">
                              <a
                                href={`https://internal.bonjour-residences.co.uk/bookings/${encodeURIComponent(row.booking_id)}`}
                                className="text-blue-600 dark:text-blue-400 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="recon-booking-link"
                              >
                                {row.booking_id}
                              </a>
                            </TableCell>
                            <TableCell className="text-sm truncate max-w-[140px] text-muted-foreground" title={row.client}>
                              {row.client}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm text-muted-foreground">
                              {formatCurrency(row.booking_value, dc)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm text-muted-foreground">
                              {formatCurrency(row.invoiced_total, dc)}
                            </TableCell>
                            <TableCell className={`text-right font-mono text-sm text-muted-foreground ${row.delta > 0 ? 'text-purple-400' : row.delta < 0 ? 'text-red-400' : ''}`}>
                              {row.delta > 0 ? '+' : ''}{formatCurrency(row.delta, dc)}
                            </TableCell>
                            <TableCell className="text-right text-sm text-muted-foreground">-</TableCell>
                            <TableCell>
                              <StatusChip status={row.recon_status} />
                            </TableCell>
                            <TableCell>
                              <span className="text-[10px] text-muted-foreground italic">pro-rata</span>
                            </TableCell>
                            <TableCell className="text-right text-sm text-muted-foreground">
                              {row.linked_invoices?.length || 0}
                            </TableCell>
                          </TableRow>
                          {expandedBooking === row.booking_id && row.linked_invoices?.length > 0 && (
                            <InvoiceExpansionRow row={row} dc={dc} />
                          )}
                        </React.Fragment>
                      ))}
                    </>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {!loading && sorted.length > 0 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground pt-2 border-t">
          <span>{sorted.length} enquiry(s)</span>
          <div className="flex gap-4 font-mono text-xs">
            <span>Closed: {formatCurrency(sorted.reduce((s, g) => s + (g.rootRow.booking_value || 0), 0), dc)}</span>
            <span>Invoiced: {formatCurrency(sorted.reduce((s, g) => s + (g.rootRow.invoiced_total || 0), 0), dc)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
